const { Age } = require("../Constants");
const LeaderNames = require("../Names/LeaderNames");
const { CivilCardTypes } = require("../Names/CardTypes");
const { Add, AddThrough, AddPerWorker, CardsOf, AddCostModifier, CostKinds, AddTrigger, TriggerEvents,
    AddResourcePool, PoolKinds, TurnFlags, AddRuleException, Rules, AddSituationalStrength, Situations, AddRestrictedCivilAction,
    Level, WorkersOn, Built, Best, MilitaryTypes, Lab, Library, Temple, Theater } = require("../../Effects/Effects");
const { Farm, Mine } = require('../TechnologyTypes').Production;
const { Arena } = require('../TechnologyTypes').Urban;
const { GetRequiredHappyFaces } = require('../../Utilities/Utilities');

// The portrait's image key; bonuses: the fixed bonuses printed along the bottom of the card (applied automatically
// while in play, see PlayerState.BuildDelegates); varies: stats the leader changes by an amount that depends on the
// board (shown as "?" along the bottom); the card text (the rest of the card, from the BGO card pages); and
// effects(player, card): the leader's other contributions to the player's numbers while in play (see Effects/Effects.js);
// expansion: for a base game leader the New Wonders and Leaders expansion changed, what its version changes (see
// Constants/CardVersions.js).
// TODO: effects that change costs, trigger on actions, or add actions (marked on each card)
const Leaders = {
    [LeaderNames.AlexanderTheGreat]: { age: Age.AgeA, image: "Alexander", varies: ['strength'],
        text: "You have +1 strength for each of your military units. As a political action, you may remove Alexander from play and add 1 yellow token from the box to your yellow bank.",
        // The political action: Moves AlexanderRetire
        effects: (player, card) => AddPerWorker(player, card, 'strength', CardsOf(player, MilitaryTypes), () => 1) },
    [LeaderNames.Aristotle]: { age: Age.AgeA, image: "Aristotle",
        text: "Every time you take a technology card from the card row, you score 1 science.",
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.TakeCard, (p, { card: taken }) => {
            if (taken.type !== CivilCardTypes.Technology)
            {
                return null;
            }
            p.science += 1;
            return "+1 science";
        }, (p, { card: taken }) => taken?.type === CivilCardTypes.Technology ? "+1 science" : null) },
    [LeaderNames.Hammurabi]: { age: Age.AgeA, image: "Hammurabi",
        text: "On your turn, you may use one of your military actions as a civil action. Taking a leader from the card row costs you 1 civil action less.",
        effects: (player, card) => {
            AddCostModifier(player, card, CostKinds.TakeCard, ({ card: taken }) => taken.type === CivilCardTypes.Leader ? -1 : 0);
            HammurabiAction(player, card);
        },
        expansion: { text: "On your turn, you may use one of your military actions as a civil action.",
            effects: (player, card) => HammurabiAction(player, card) } },
    [LeaderNames.Homer]: { age: Age.AgeA, image: "Homer", bonuses: { happyFaces: 1 },
        text: "Each turn, you have an extra 1 resource for building and upgrading military units. When you replace Homer, you may give up the returned civil action to add 1 happy face to one of your completed wonders.",
        // The choice when replaced: Moves PlayLeader (homerWonderId)
        effects: (player, card) => AddResourcePool(player, card, PoolKinds.MilitaryResources, 1) },
    [LeaderNames.JuliusCaesar]: { age: Age.AgeA, image: "Caesar", bonuses: { militaryActions: 1, strength: 1 },
        text: "After you play a political action, you may play another political action. This ability can be used only once per game.",
        // The second political action: Moves EndPoliticalAction
        oncePerGame: true,
        expansion: { text: "After you play a political action, you may play another political action.", oncePerGame: false } },
    [LeaderNames.Moses]: { age: Age.AgeA, image: "Moses",
        text: "Increasing your population costs you 1 food less.",
        effects: (player, card) => AddCostModifier(player, card, CostKinds.PopulationFood, -1) },

    // ---- Age A, New Wonders and Leaders
    [LeaderNames.Boudica]: { age: Age.AgeA, image: "Boudica",
        text: "At the start of your action phase: if you are one of the two weakest civilizations (the weakest with 2 players), you have 2 extra resources for building and upgrading military units this turn. Otherwise, gain 1 food, 1 resource or 1 science.",
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.ActionPhaseStart, (p) => {
            if (require('../../Moves/EventEffects').Weakest(p.gameState, 2).includes(p))
            {
                p.turnPools.push({ source: card.name, kind: PoolKinds.MilitaryResources, amount: 2 });
                return "+2 resources for military units this turn";
            }
            require('../../Moves/Decisions').RequestDecision(p.gameState, {
                player: p, type: 'gainOne', event: card.name, prompt: "Boudica: gain 1 food, 1 resource or 1 science",
                options: [['food', '1 food'], ['resources', '1 resource'], ['science', '1 science']].map(([value, label]) => ({ label, value })),
                context: { amount: 1 },
            });
            return "gains 1 food, resource or science (a choice)";
        }) },
    [LeaderNames.Cleopatra]: { age: Age.AgeA, image: "Cleopatra",
        text: "During your action phase, you have an extra resource for building wonders or for building and upgrading urban buildings.",
        effects: (player, card) => AddResourcePool(player, card, PoolKinds.WonderUrbanResources, 1) },
    [LeaderNames.Hippocrates]: { age: Age.AgeA, image: "Hippocrates",
        text: "At the start of your turn, take a yellow token from the box and put it above your government card (up to 3). After losing yellow tokens at the start of Ages II, III and IV, put one of these tokens into your yellow bank.",
        // (the tokens stay above the government card after he leaves play; see GameState.RetireAge)
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.TurnStart, (p) => {
            const kept = p.CardState(card.name);
            if ((kept.tokens ?? 0) >= 3)
            {
                return null;
            }
            kept.tokens = (kept.tokens ?? 0) + 1;
            return `+1 yellow token above the government card (${kept.tokens} of 3)`;
        }) },
    [LeaderNames.Confucius]: { age: Age.AgeA, image: "Confucius",
        text: "Whenever you prepare an event, and when Confucius leaves play, score 1 science. You can prepare non-event military cards as events. When revealed, such a card means: \"Each civilization gains 1 science.\"",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.PrepareAnyMilitaryCard);
            AddTrigger(player, card, TriggerEvents.PrepareEvent, (p) => {
                p.science += 1;
                return "+1 science";
            }, () => "+1 science");
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader }) => {
                if (leader?.name !== card.name)
                {
                    return null;
                }
                p.science += 1;
                return "+1 science on leaving play";
            });
        } },
    [LeaderNames.SunTzu]: { age: Age.AgeA, image: "SunTzu", bonuses: { strength: 1 },
        text: "You skip making your tactic available at the start of your action phase. At the end of your turn, draw 2 additional military cards. When Sun Tzu leaves play, slide him under your current tactic: that tactic card is permanently worth +1 strength per army (for whoever is on that card).",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.KeepTacticPrivate);
            AddTrigger(player, card, TriggerEvents.EndOfTurn, (p) => {
                const drawn = p.gameState.DrawMilitaryCards(p, 2);
                return drawn > 0 ? `+${drawn} military card${drawn === 1 ? '' : 's'}` : null;
            });
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader }) => {
                const id = p.exclusiveTactic?.id ?? p.currentTacticId;
                if (leader?.name !== card.name || id === null || id === undefined)
                {
                    return null;
                }
                p.gameState.tacticBonuses[id] = (p.gameState.tacticBonuses[id] ?? 0) + 1;
                p.currentTacticId = id;
                return `goes under ${p.currentTactic}: that card is worth +${p.gameState.tacticBonuses[id]} strength per army`;
            });
        } },
    [LeaderNames.Ashoka]: { age: Age.AgeA, image: "Ashoka", varies: ['food', 'resources'],
        text: "If you have civil cards of two different colors in your hand, you produce an extra resource. If three or more colors, you produce an extra food and an extra resource.",
        effects: (player, card) => {
            Add(player, card, 'resources', p => HandColors(p) >= 2 ? 1 : 0);
            Add(player, card, 'food', p => HandColors(p) >= 3 ? 1 : 0);
        } },

    [LeaderNames.ChristopherColumbus]: { age: Age.Age1, image: "Columbus",
        text: "As a political action, you may remove Columbus from play to colonize a territory in your hand without sacrificing any unit." },
        // The political action: Moves ColumbusColonize
    [LeaderNames.FrederickBarbarossa]: { age: Age.Age1, image: "Barbarossa",
        text: "For 1 military action, you can increase your population and build a new military unit with it. This costs you 1 food and 1 resource less than usual." },
        // The action: Moves BarbarossaRecruit
    [LeaderNames.GenghisKhan]: { age: Age.Age1, image: "Genghis",
        text: "One or more of your infantry units can be considered cavalry for purposes of tactics. At the end of your turn, score 3 culture if you are one of the 2 strongest civilizations (you win ties).",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.InfantryAsCavalry);
            AddTrigger(player, card, TriggerEvents.EndOfTurn, (p) => {
            // Ties go to Genghis: only rivals strictly stronger push him out of the top 2 (the top 1 with two players in the
            // game: "the two strongest" means the strongest then)
            const strength = p.GetMilitaryStrength();
            const rivals = (p.gameState?.playerStates ?? []).filter(other => other !== p && !other.resigned);
            const stronger = rivals.filter(other => other.GetMilitaryStrength() > strength).length;
            if (stronger >= Math.min(2, rivals.length))
            {
                return { note: `no culture this turn: ${stronger} civilizations are stronger` };
            }
            p.culture += 3;
            return "+3 culture (one of the 2 strongest)";
            });
        } },
    [LeaderNames.JoanOfArc]: { age: Age.Age1, image: "Joan", bonuses: { militaryActions: 1, culture: 1 }, varies: ['strength'],
        text: "Your temples and government give you +1 strength for each of their happy faces. At the beginning of your political phase, you may look at the top current event card.",
        // Looking at the top current event: Server/Authority JoanPeek (hidden from everyone else)
        effects: (player, card) => {
            AddPerWorker(player, card, 'strength', CardsOf(player, [Temple]), (p, temple) => temple.happyFaces);
            AddThrough(player, card, 'strength', [player.governmentTechnology], HappyFacesOf);
        } },
    [LeaderNames.LeonardoDaVinci]: { age: Age.Age1, image: "DaVinci", varies: ['science'],
        text: "Your best lab or library produces an additional amount of science equal to its level. Every time you develop a technology, gain 1 resource.",
        effects: (player, card) => {
            AddBestLabOrLibraryScience(player, card);
            AddTrigger(player, card, TriggerEvents.Develop, (p) => {
                p.GainResources(1);
                return "+1 resource";
            }, () => "+1 resource");
        } },
    [LeaderNames.Michelangelo]: { age: Age.Age1, image: "Michelangelo", varies: ['culture'],
        text: "Your temples, theaters, and wonders produce an extra 1 culture for each of their happy faces. When taking a wonder from the card row, you do not pay extra actions for the wonders you have.",
        effects: (player, card) => MichelangeloEffects(player, card, [Temple, Theater]),
        expansion: { text: "Your urban buildings and wonders produce an extra 1 culture for each of their happy faces. When taking a wonder from the card row, you do not pay extra actions for the wonders you have, and you gain 1 resource.",
            effects: (player, card) => {
                MichelangeloEffects(player, card, UrbanTypes);
                AddTrigger(player, card, TriggerEvents.TakeCard, (p, { card: taken }) => {
                    if (taken.type !== CivilCardTypes.Wonder)
                    {
                        return null;
                    }
                    p.GainResources(1);
                    return "+1 resource";
                }, (p, { card: taken }) => taken?.type === CivilCardTypes.Wonder ? "+1 resource" : null);
            } } },

    // ---- Age I, New Wonders and Leaders
    [LeaderNames.IsabellaOfCastile]: { age: Age.Age1, image: "Isabella", bonuses: { resources: 1, blueTokens: 2 },
        text: "You produce an extra resource and gain two extra blue tokens. During colonization, you may pay 1, 3 or 6 resources to gain +1, +2 or +3 colonization points." },
        // The colonization points: Moves/Colonization.js (a payment's isabella)
    [LeaderNames.EleanorOfAquitaine]: { age: Age.Age1, image: "Eleanor",
        text: "Whenever you prepare an event, draw two military cards. Score 1 culture for each military card you have to discard at the end of your turn. When Eleanor is replaced, get 3 civil actions back instead of 1.",
        effects: (player, card) => {
            AddTrigger(player, card, TriggerEvents.PrepareEvent, (p) => {
                const drawn = p.gameState.DrawMilitaryCards(p, 2);
                return drawn > 0 ? `+${drawn} military card${drawn === 1 ? '' : 's'}` : null;
            }, () => "+2 military cards");
            AddTrigger(player, card, TriggerEvents.DiscardMilitary, (p, { count }) => {
                p.culture += count;
                return `+${count} culture (${count} military card${count === 1 ? '' : 's'} discarded)`;
            });
            // Replaced (not when she leaves at a new age): 2 more of the civil actions spent this turn back
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader, replaced }) => {
                const back = Math.min(2, p.civilActionsUsed);
                if (leader?.name !== card.name || !replaced || back === 0)
                {
                    return null;
                }
                p.civilActionsUsed -= back;
                return `${back + 1} civil actions back`;
            });
        } },
    [LeaderNames.JanZizka]: { age: Age.Age1, image: "Zizka", bonuses: { militaryActions: 1 }, varies: ['culture'],
        text: "You gain an extra military action. For purposes of tactics, each of your farms can be considered an Age A infantry or artillery unit. Each of your armies produces 1 culture.",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.FarmsAsUnits);
            // (the armies formed for the current tactic, full or obsolete; see PlayerState.GetTacticArmies)
            Add(player, card, 'culture', p => {
                const armies = p.GetTacticArmies();
                return armies ? armies.full + armies.obsolete : 0;
            });
        } },
    [LeaderNames.Nostradamus]: { age: Age.Age1, image: "Nostradamus",
        text: "When you prepare an event, score an extra 3 culture. When another player prepares an event, look at it. When determining who is the weakest during events, or defending against aggressions, you have +3 strength.",
        // Looking at the events others prepare: Server/Authority NostradamusSees (hidden from everyone else)
        effects: (player, card) => {
            AddTrigger(player, card, TriggerEvents.PrepareEvent, (p) => {
                p.culture += 3;
                return "+3 culture";
            }, () => "+3 culture");
            AddSituationalStrength(player, card, Situations.EventWeakest, 3);
            AddSituationalStrength(player, card, Situations.DefendAggression, 3);
        } },
    [LeaderNames.JohannesGutenberg]: { age: Age.Age1, image: "Gutenberg",
        text: "Each turn, you have an extra civil action for taking, developing, building or upgrading your labs and libraries, with a discount of 1 science or 1 resource. Whenever you use this action, score 2 culture.",
        effects: (player, card) => {
            // (unused this turn, and not declined for this move)
            const unused = (p) => !p.turnFlags.includes(TurnFlags.Gutenberg) && !p.restrictedDeclined;
            AddRestrictedCivilAction(player, card, {
                flag: TurnFlags.Gutenberg,
                purpose: "taking, developing, building or upgrading labs and libraries",
                eligible: ({ card: taken, tech }) => IsLabOrLibrary(taken ?? tech),
                onUse: (p) => {
                    p.culture += 2;
                    return "his civil action, +2 culture";
                },
                gain: "+2 culture",
            });
            // The discount comes with his civil action (used first for these, while unused)
            AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }, p) => IsLabOrLibrary(tech) && unused(p) ? -1 : 0);
            AddCostModifier(player, card, CostKinds.BuildPayment, ({ tech }, p) => IsLabOrLibrary(tech) && unused(p) ? -1 : 0);
        } },
    [LeaderNames.Saladin]: { age: Age.Age1, image: "Saladin", varies: ['strength', 'civilActions'],
        text: "Immediately after your action phase, decide either to increase your strength by 2 or to gain 1 civil action. Either lasts until the next decision or until Saladin leaves play.",
        // The decision: Moves EndTurn (saladin)
        effects: (player, card) => {
            Add(player, card, 'strength', p => p.CardState(card.name).choice === 'strength' ? 2 : 0);
            Add(player, card, 'civilActions', p => p.CardState(card.name).choice === 'civil' ? 1 : 0);
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader }) => {
                if (leader?.name === card.name)
                {
                    delete p.CardState(card.name).choice;
                }
                return null;
            });
        } },

    [LeaderNames.IsaacNewton]: { age: Age.Age2, image: "Newton", varies: ['science'],
        text: "Your best lab or library produces an additional amount of science equal to its level. Every time you develop a technology, you get back 1 spent civil action.",
        effects: (player, card) => {
            AddBestLabOrLibraryScience(player, card);
            AddTrigger(player, card, TriggerEvents.Develop, (p) => {
                if (p.civilActionsUsed < 1)
                {
                    return null;
                }
                p.civilActionsUsed--;
                return "1 civil action back";
            }, () => "1 civil action back");
        } },
    [LeaderNames.JamesCook]: { age: Age.Age2, image: "JamesCook",
        text: "When colonizing, you may discard up to 2 military cards to get +1 colonization for each. One of your colonies produces 2 culture. Every colony after that produces 1 culture.",
        // The discards: when paying for a colony (Moves/Colonization.js). The culture is the colonies' own.
        effects: (player, card) => AddThrough(player, card, 'culture', player.colonies, (p, colony) => p.colonies.indexOf(colony) === 0 ? 2 : 1) },
    [LeaderNames.JohannesSebastianBach]: { age: Age.Age2, image: "Bach", varies: ['culture'],
        text: "Each of your theaters produces an extra 1 culture. It costs you 2 less science to develop theater technologies. Once during your turn, you may spend a civil action to upgrade any urban building to a theater of the same level or higher for the usual cost.",
        // The upgrade: Moves BachUpgrade
        effects: (player, card) => {
            AddPerWorker(player, card, 'culture', CardsOf(player, [Theater]), () => 1);
            AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }) => tech.type === Theater ? -2 : 0);
        },
        expansion: { bonuses: { culture: 2 } } },
    [LeaderNames.MaximillienRobespierre]: { age: Age.Age2, image: "Robespierre", bonuses: { militaryActions: 1 },
        text: "A revolution costs you all of your military actions instead of all of your civil actions. You score 3 culture when declaring a revolution.",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.RevolutionWithMilitaryActions);
            AddTrigger(player, card, TriggerEvents.Revolution, (p) => {
                p.culture += 3;
                return "+3 culture";
            });
        } },
    [LeaderNames.NapoleonBonaparte]: { age: Age.Age2, image: "Napoleon", bonuses: { militaryActions: 2 }, varies: ['strength'],
        text: "You have +2 strength for each different type of military unit you have (infantry, cavalry, artillery and air force).",
        effects: (player, card) => Add(player, card, 'strength', p => 2 * MilitaryTypes.filter(type => WorkersOn(p, [type]) > 0).length),
        expansion: { bonuses: { militaryActions: 1 } } },
    [LeaderNames.WilliamShakespeare]: { age: Age.Age2, image: "Shakespeare", bonuses: { happyFaces: 1 }, varies: ['culture'],
        text: "If you have a library, you can build theaters for 1 resource less and develop theater technologies for 1 science less. Vice versa if you have a theater. Each library-theater pair produces an extra 2 culture.",
        effects: (player, card) => ShakespeareEffects(player, card, 1, 1),
        expansion: { text: "If you have a library, you can build theaters for 2 resources less and develop theater technologies for 3 science less. Vice versa if you have a theater. Each library-theater pair produces an extra 2 culture.",
            effects: (player, card) => ShakespeareEffects(player, card, 2, 3) } },

// ---- Age II, New Wonders and Leaders
    [LeaderNames.CatherineTheGreat]: { age: Age.Age2, image: "Catherine", bonuses: { militaryActions: 1, culture: 2 },
        text: "Gain an extra military action and produce 2 culture. Once per game, as a political action, you may cause each civilization with a lower strength rating to lose 1 yellow token. If at least one token is lost (two in a 4-player game), you gain 1 yellow token." },
        // The political action: Moves CatherineDecree
    [LeaderNames.MariaTheresa]: { age: Age.Age2, image: "MariaTheresa",
        text: "Whenever you gain or increase your population, score 1 science and 1 culture. At the start of your action phase, you get 1 resource for building and upgrading military units (3 if you are one of the two weakest civilizations).",
        effects: (player, card) => {
            AddTrigger(player, card, TriggerEvents.PopulationIncrease, (p) => {
                p.science += 1;
                p.culture += 1;
                return "+1 science, +1 culture";
            }, () => "+1 science, +1 culture");
            AddTrigger(player, card, TriggerEvents.ActionPhaseStart, (p) => {
                const amount = require('../../Moves/EventEffects').Weakest(p.gameState, 2).includes(p) ? 3 : 1;
                p.turnPools.push({ source: card.name, kind: PoolKinds.MilitaryResources, amount });
                return `+${amount} resource${amount === 1 ? '' : 's'} for military units this turn${amount === 3 ? ' (one of the two weakest)' : ''}`;
            });
        } },
    [LeaderNames.JamesWatt]: { age: Age.Age2, image: "Watt",
        text: "Your farms and mines and their technologies have a reduced cost: in Age II, 2 resources and 2 science less; in Age III, 4 resources and 4 science less. When Watt leaves play, score culture equal to the production of your best farm or mine.",
        effects: (player, card) => {
            // (by the card's own age)
            const off = (tech) => FarmsAndMines.includes(tech?.type) ? -(WattDiscount[tech.age] ?? 0) : 0;
            AddCostModifier(player, card, CostKinds.BuildCost, ({ tech }) => off(tech));
            AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }) => off(tech));
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader }) => {
                if (leader?.name !== card.name)
                {
                    return null;
                }
                // What the best farm or mine produces: a worker's production, with what other cards add through it (Machu
                // Picchu, Transcontinental Railroad: an Age I mine can beat an Age II one)
                const through = (tech) => [...p.foodProductionCalculationDelegate, ...p.resourceProductionCalculationDelegate]
                    .filter(d => d.Through === tech).reduce((sum, d) => sum + d.Action(), 0);
                const best = Built(p, FarmsAndMines).reduce((most, tech) => Math.max(most, (tech.multiplier ?? 0) + through(tech)), 0);
                p.culture += best;
                return best > 0 ? `+${best} culture (his best farm or mine's production)` : null;
            });
        } },
    [LeaderNames.AlfredNobel]: { age: Age.Age2, image: "Nobel", bonuses: { strength: 2, science: 2 },
        text: "You gain +2 strength and produce +2 science. If Nobel is replaced, do not get 1 civil action back. Instead, put this card by the culture track. From then on, any civilization scores 4 culture at the end of a turn in which it developed at least 2 technologies.",
        // The prize, once he is by the culture track: Moves NobelPrize (at the end of every turn)
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p, { leader, replaced }) => {
            if (leader?.name !== card.name || !replaced)
            {
                return null;
            }
            // (replacing a leader gives a civil action back: not him)
            p.civilActionsUsed += 1;
            p.gameState.nobelPrize = true;
            p.gameState.reports.push({ kind: 'notice', name: card.name, round: p.gameState.round, changes: [], journal: 'Game',
                text: "Alfred Nobel is put by the culture track: from now on, a civilization scores 4 culture at the end of a turn in which it developed at least 2 technologies." });
            return "no civil action back; he goes by the culture track (the Nobel Prize)";
        }) },
    [LeaderNames.AntoniGaudi]: { age: Age.Age2, image: "Gaudi", varies: ['culture'],
        text: "Developing an urban building technology costs you 1 science less for each other type of urban building you have. Your best urban building of each type produces 1 extra culture.",
        effects: (player, card) => {
            AddCostModifier(player, card, CostKinds.DevelopScience, ({ tech }, p) => UrbanTypes.includes(tech?.type)
                ? -UrbanTypes.filter(type => type !== tech.type && Built(p, [type]).length > 0).length : 0);
            AddThrough(player, card, 'culture', CardsOf(player, UrbanTypes), (p, building) =>
                building.GetWorkerCount() > 0 && Best(p, [building.type]) === building ? 1 : 0);
        } },
    [LeaderNames.CharlesDarwin]: { age: Age.Age2, image: "Darwin", varies: ['culture', 'happyFaces'],
        text: "Each of your temples gives you one happy face less. Each of your labs and libraries produces 1 extra culture. Taking a technology card costs you 1 civil action less, but no less than 1.",
        effects: (player, card) => {
            AddPerWorker(player, card, 'happyFaces', CardsOf(player, [Temple]), () => -1);
            AddPerWorker(player, card, 'culture', CardsOf(player, [Lab, Library]), () => 1);
            AddCostModifier(player, card, CostKinds.TakeCard, ({ card: taken, base }) => taken.type === CivilCardTypes.Technology && base > 1 ? -1 : 0);
        } },

    [LeaderNames.AlbertEinstein]: { age: Age.Age3, image: "Einstein", varies: ['science'],
        text: "Your best lab or library produces an additional amount of science equal to its level. Every time you develop a technology, you score 3 culture.",
        effects: (player, card) => {
            AddBestLabOrLibraryScience(player, card);
            AddTrigger(player, card, TriggerEvents.Develop, (p) => {
                p.culture += 3;
                return "+3 culture";
            }, () => "+3 culture");
        } },
    [LeaderNames.BillGates]: { age: Age.Age3, image: "BillGates", varies: ['resources'],
        text: "Each of your labs also produces resources equal to its level. When Bill Gates leaves play or at the end of the game, score culture equal to this additional production.",
        // His labs of Age I or higher are used as mines: when mines produce, each worker on one puts a blue token on it, worth
        // its level; they store resources and make change (Code of Laws appendix)
        effects: (player, card) => {
            AddRuleException(player, card, Rules.LabsAsMines);
            AddPerWorker(player, card, 'resources', CardsOf(player, [Lab]), (p, lab) => Level(lab));
            CardsOf(player, [Lab]).filter(lab => Level(lab) > 0).forEach(lab => player.resourceProductionDelegate.push({
                Source: card, Priority: Level(lab),
                Action: () => lab.AddContent("blueCubes", Math.min(lab.GetWorkerCount(), player.blueBank)),
            }));
            AddTrigger(player, card, TriggerEvents.LeaderLeaves, (p) => {
                const culture = LabLevels(p);
                p.culture += culture;
                return culture > 0 ? `+${culture} culture on leaving play` : null;
            });
            AddTrigger(player, card, TriggerEvents.GameEnd, (p) => {
                const culture = LabLevels(p);
                p.culture += culture;
                return culture > 0 ? `+${culture} culture at the end of the game` : null;
            });
        } },
    [LeaderNames.CharlesChaplin]: { age: Age.Age3, image: "Chaplin", bonuses: { happyFaces: 2 }, varies: ['culture'],
        text: "Your best theater produces twice as much culture.",
        effects: (player, card) => AddThrough(player, card, 'culture', CardsOf(player, [Theater]),
            (p, theater) => Best(p, [Theater]) === theater ? theater.multiplier : 0) },
    [LeaderNames.MahatmaGandhi]: { age: Age.Age3, image: "Gandhi", bonuses: { culture: 2 },
        text: "You may not play aggression or war cards. Your rivals must pay twice as many military actions to declare aggression or war against you.",
        effects: (player, card) => {
            AddRuleException(player, card, Rules.NoAggression);
            AddRuleException(player, card, Rules.DoubleAggressionCost);
        },
        expansion: { text: "You may not play aggression or war cards. Your rivals must pay 3 more military actions to declare aggression or war against you.",
            effects: (player, card) => {
                AddRuleException(player, card, Rules.NoAggression);
                AddRuleException(player, card, Rules.ExtraAggressionCost);
            } } },
    [LeaderNames.SidMeier]: { age: Age.Age3, image: "SidMeier", varies: ['culture', 'science'],
        text: "Each of your labs produces an amount of culture equal to its level. However, each produces 1 science less.",
        effects: (player, card) => {
            AddPerWorker(player, card, 'culture', CardsOf(player, [Lab]), (p, lab) => Level(lab));
            AddPerWorker(player, card, 'science', CardsOf(player, [Lab]), () => -1);
        } },
    [LeaderNames.WinstonChurchill]: { age: Age.Age3, image: "Churchill",
        text: "On your turn, choose one: score 3 culture; or you have 3 science and 3 resources for developing military unit technologies and building and upgrading military units.",
        // Taking the science and resources is an action (Moves ChurchillTake); otherwise he scores at the end of the turn
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.EndOfTurn, (p) => {
            if (p.turnFlags.includes(TurnFlags.Churchill))
            {
                return { note: "no culture this turn: you took his science and resources" };
            }
            p.culture += 3;
            return "+3 culture";
        }) },
}

// Bill Gates and Sid Meier: the level of each lab, once per worker on it
function LabLevels(player)
{
    return Built(player, [Lab]).reduce((sum, lab) => sum + Level(lab) * lab.GetWorkerCount(), 0);
}

// Da Vinci, Newton and Einstein: the best lab or library with a worker on it produces extra science equal to its level
function AddBestLabOrLibraryScience(player, card)
{
    AddThrough(player, card, 'science', CardsOf(player, [Lab, Library]),
        (p, building) => Best(p, [Lab, Library]) === building ? Level(building) : 0);
}

// The colors of the civil cards in a hand, as printed (Ashoka): actions yellow, leaders green, and technologies by kind:
// farms and mines brown, urban buildings grey, military red, special technologies blue, governments orange. (Wonders are
// never in a hand: taking one starts building it.)
// ---- Age III, New Wonders and Leaders (added to the table below)
const AgeThreeLeaders = {
    [LeaderNames.MarieCurie]: { age: Age.Age3, image: "MarieCurie", varies: ['science', 'strength', 'culture'],
        text: "Your best mine produces science equal to its level. Your best lab gives you strength equal to its level. Sklodowska produces culture equal to the better of these two.",
        effects: (player, card) => {
            AddThrough(player, card, 'science', CardsOf(player, [Mine]), (p, mine) => Best(p, [Mine]) === mine ? Level(mine) : 0);
            AddThrough(player, card, 'strength', CardsOf(player, [Lab]), (p, lab) => Best(p, [Lab]) === lab ? Level(lab) : 0);
            Add(player, card, 'culture', p => Math.max(Best(p, [Mine]) ? Level(Best(p, [Mine])) : 0, Best(p, [Lab]) ? Level(Best(p, [Lab])) : 0));
        } },
    [LeaderNames.MarleneDietrich]: { age: Age.Age3, image: "Dietrich", varies: ['culture', 'happyFaces'],
        text: "Each of your best theaters produces an extra 1 culture and 1 happy face. One of your infantry, cavalry, or artillery units counts twice for purposes of tactics.",
        effects: (player, card) => {
            // (each building on the best theater card)
            const onBest = (p, theater) => Best(p, [Theater]) === theater ? 1 : 0;
            AddPerWorker(player, card, 'culture', CardsOf(player, [Theater]), onBest);
            AddPerWorker(player, card, 'happyFaces', CardsOf(player, [Theater]), onBest);
            AddRuleException(player, card, Rules.DoubleOneUnit);
        } },
    [LeaderNames.SteveJobs]: { age: Age.Age3, image: "SteveJobs", varies: ['happyFaces'],
        text: "Your best lab gives you happy faces equal to its level. Whenever you develop a technology, you may pay 1 resource to score 4 culture.",
        effects: (player, card) => {
            AddThrough(player, card, 'happyFaces', CardsOf(player, [Lab]), (p, lab) => Best(p, [Lab]) === lab ? Level(lab) : 0);
            AddTrigger(player, card, TriggerEvents.Develop, (p) => {
                if (p.GetResources() < 1)
                {
                    return { note: "no resource to pay for 4 culture" };
                }
                require('../../Moves/Decisions').RequestDecision(p.gameState, {
                    player: p, type: 'jobsCulture', event: card.name, prompt: "Steve Jobs: pay 1 resource to score 4 culture?",
                    options: [{ label: "Pay 1 resource: +4 culture", value: true }, { label: "No", value: false }],
                });
                return null;
            }, (p) => p.GetResources() >= 1 ? "may pay 1 resource for 4 culture" : null);
        } },
    [LeaderNames.NelsonMandela]: { age: Age.Age3, image: "Mandela", bonuses: { civilActions: 1, culture: 2 },
        text: "You gain an extra civil action and produce 2 culture. If you have no discontent workers, taking this card costs 3 civil actions more. At the end of your turn, score 1 culture for each surplus happy face.",
        // (3 more, as the rulebook has it)
        takeCardAdjustment: (player) => player.GetDiscontentWorkers() === 0 ? 3 : 0,
        effects: (player, card) => AddTrigger(player, card, TriggerEvents.EndOfTurn, (p) => {
            const surplus = Math.max(0, p.GetHappyFaces() - GetRequiredHappyFaces(p.yellowBank));
            if (surplus === 0)
            {
                return { note: "no surplus happy faces" };
            }
            p.culture += surplus;
            return `+${surplus} culture (${surplus} surplus happy face${surplus === 1 ? '' : 's'})`;
        }) },
    [LeaderNames.PierreDeCoubertin]: { age: Age.Age3, image: "Coubertin", varies: ['culture'],
        text: "Each of your arenas produces 2 culture. Once per game, before your political action, you may declare the Olympic Games: score 8 culture, and until the start of your next turn, whoever plays a war or aggression loses 8 culture.",
        // The Olympic Games: Moves CoubertinOlympics
        effects: (player, card) => AddPerWorker(player, card, 'culture', CardsOf(player, [Arena]), () => 2) },
    [LeaderNames.IanFleming]: { age: Age.Age3, image: "Fleming", varies: ['culture'],
        text: "Your best library and theater each produce extra culture equal to their levels. At the beginning of your political phase, you may look at one player's military cards. You cannot choose the same player on two consecutive turns.",
        // Looking: the decision flemingPeek, shown to him by the server (Server/Authority FlemingSees)
        effects: (player, card) => {
            AddThrough(player, card, 'culture', CardsOf(player, [Library, Theater]), (p, building) => Best(p, [building.type]) === building ? Level(building) : 0);
            AddTrigger(player, card, TriggerEvents.TurnStart, (p) => {
                const state = p.gameState;
                // (with the political phase given up for International Agreement, he can still look)
                if (state.phase !== 'Political' && !p.turnFlags.includes(TurnFlags.PoliticalGivenUp))
                {
                    return null;
                }
                const rivals = state.playerStates.filter(other => other !== p && !other.resigned && other.playerNumber !== p.CardState(card.name).lastTarget);
                require('../../Moves/Decisions').RequestDecision(state, {
                    player: p, type: 'flemingPeek', event: card.name, prompt: "Ian Fleming: look at one player's military cards?",
                    // (their military hand as card backs, by age)
                    options: [...rivals.map(other => ({ label: `${other.name} ${other.militaryHand.map(c => require('../../Utilities/CardRefs').HiddenRef(c.age)).join(' ') || '(no cards)'}`, value: other.playerNumber })),
                        { label: "No one", value: null }],
                });
                return null;
            });
        } },
};

// James Watt's discount on farms and mines (resources to build, science to develop), by the card's age
const WattDiscount = { [Age.Age2]: 2, [Age.Age3]: 4 };
const FarmsAndMines = [Farm, Mine];
const UrbanTypes = Object.values(require('../TechnologyTypes').Urban);

// A lab or library: a technology card (in the row or hand: its technology) or a technology on the board (Gutenberg)
const IsLabOrLibrary = (card) => [Lab, Library].includes(card?.Technology?.type ?? card?.type);

const CardColor = (card) => {
    if (card.type !== CivilCardTypes.Technology)
    {
        return card.type;
    }
    const kind = card.Technology?.type;
    const TechnologyTypes = require('../TechnologyTypes');
    if (kind === TechnologyTypes.Production.Farm || kind === TechnologyTypes.Production.Mine) return 'production';
    if (Object.values(TechnologyTypes.Urban).includes(kind)) return 'urban';
    if (Object.values(TechnologyTypes.Military).includes(kind)) return 'military';
    if (Object.values(TechnologyTypes.Special).includes(kind)) return 'special';
    return 'government';
};
const HandColors = (player) => new Set(player.civilHand.map(CardColor)).size;

// The happy faces a card gives by itself (Joan of Arc, Michelangelo)
function HappyFacesOf(player, card)
{
    return player.happyFaceCalculationDelegate
        .filter(d => d.Source === card && d.Through === undefined)
        .reduce((sum, d) => sum + Math.max(0, d.Action()), 0);
}

// Shakespeare's extra culture comes from each library and theater working as a pair, not from one card
const LibraryTheaterPairs = { name: "Library–theater pairs", type: Theater };

// Shakespeare: the pairs' culture, and theaters cheaper with a library built and libraries with a theater built (by
// resources to build and science to develop: 1 and 1 in the base game, 2 and 3 in the expansion's version)
function ShakespeareEffects(player, card, resources, science)
{
    AddThrough(player, card, 'culture', [LibraryTheaterPairs], p => 2 * Math.min(WorkersOn(p, [Library]), WorkersOn(p, [Theater])));
    const paired = ({ tech }, p) => (tech.type === Theater && WorkersOn(p, [Library]) > 0)
        || (tech.type === Library && WorkersOn(p, [Theater]) > 0);
    AddCostModifier(player, card, CostKinds.BuildCost, (context, p) => paired(context, p) ? -resources : 0);
    AddCostModifier(player, card, CostKinds.DevelopScience, (context, p) => paired(context, p) ? -science : 0);
}

// Hammurabi: an extra civil action, once per turn, while a military action is left to pay for it (see SpendCivilActions)
function HammurabiAction(player, card)
{
    player.civilActionCalculationDelegate.push({
        Source: card,
        MilitaryAsCivil: true,
        Action: () => !player.turnFlags.includes(TurnFlags.MilitaryAsCivil) && player.GetMilitaryActionsRemaining() >= 1 ? 1 : 0,
    });
}

// Michelangelo: culture for the happy faces of these buildings (temples and theaters in the base game, every urban
// building in the expansion's version) and of wonders; no extra civil action per completed wonder when taking one
function MichelangeloEffects(player, card, buildingTypes)
{
    AddPerWorker(player, card, 'culture', CardsOf(player, buildingTypes), (p, building) => building.happyFaces ?? 0);
    AddThrough(player, card, 'culture', player.wonders, HappyFacesOf);
    AddCostModifier(player, card, CostKinds.TakeCard, ({ card: taken }, p) => taken.type === CivilCardTypes.Wonder ? -p.wonders.length : 0);
}

Object.assign(Leaders, AgeThreeLeaders);

module.exports = Leaders;
